import React from 'react';
import PropTypes from 'prop-types';
import { Loader2, Database } from 'lucide-react';

/**
 * 共通ローディング状態表示コンポーネント
 * クイズセクション全体で一貫したローディング表示を提供
 */

// 基本的なスピナー
const Spinner = ({ size = 'default', className = '' }) => {
  const sizeClasses = {
    small: 'h-4 w-4',
    default: 'h-6 w-6',
    large: 'h-8 w-8'
  };

  return (
    <Loader2 
      className={`animate-spin ${sizeClasses[size]} ${className}`}
      aria-label="読み込み中"
    />
  );
};

// インライン ローディング（小さなローディング表示）
const InlineLoading = ({ message = '読み込み中...', size = 'default' }) => (
  <div className="flex items-center justify-center gap-2 py-2">
    <Spinner size={size} className="text-purple-600" />
    <span className="text-sm text-gray-600">{message}</span>
  </div>
);

// フルエリア ローディング（大きな領域全体）
const FullAreaLoading = ({ 
  message = 'データを読み込み中...', 
  description = null,
  className = ''
}) => (
  <div className={`flex flex-col items-center justify-center py-12 ${className}`}>
    <Spinner size="large" className="text-purple-600 mb-4" />
    <p className="text-lg text-gray-700 mb-2">{message}</p>
    {description && (
      <p className="text-sm text-gray-500 text-center max-w-md">{description}</p>
    )}
  </div>
);

// オーバーレイ ローディング（モーダル的な表示）
const OverlayLoading = ({ 
  message = '処理中...', 
  description = null,
  isVisible = true 
}) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
        <div className="flex flex-col items-center">
          <Spinner size="large" className="text-purple-600 mb-4" />
          <p className="text-lg text-gray-700 text-center mb-2">{message}</p>
          {description && (
            <p className="text-sm text-gray-500 text-center">{description}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// コンテンツ読み込み中
const ContentLoading = () => (
  <FullAreaLoading 
    message="コンテンツを読み込み中..."
    description="問題データを取得しています。しばらくお待ちください。"
  />
);

// 問題読み込み中
const QuestionsLoading = () => (
  <div className="flex items-center justify-center py-8">
    <div className="text-center">
      <Spinner size="large" className="text-purple-600 mb-4 mx-auto" />
      <p className="text-lg text-gray-700 mb-2">問題を準備中...</p>
      <p className="text-sm text-gray-500">
        問題データを読み込んでいます
      </p>
    </div>
  </div>
);

// 結果保存中
const SavingResults = () => (
  <OverlayLoading
    message="結果を保存中..."
    description="もうしばらくお待ちください"
  />
);

// 割り当て処理中
const AssigningContent = () => (
  <OverlayLoading
    message="コンテンツを割り当て中..."
    description="処理が完了するまでお待ちください"
  />
);

// 結果取得中
const LoadingResults = () => (
  <div className="flex items-center justify-center py-6">
    <Database className="h-5 w-5 text-purple-600 mr-2 animate-pulse" />
    <span className="text-gray-600">結果を取得中...</span>
  </div>
);

// カードローディング（コンテンツリスト用）
const CardLoadingSkeleton = () => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
    <div className="flex justify-between items-start mb-3">
      <div className="h-6 bg-gray-200 rounded w-2/3"></div>
      <div className="h-5 bg-gray-200 rounded w-16"></div>
    </div>
    <div className="space-y-2 mb-4">
      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      <div className="h-4 bg-gray-200 rounded w-1/3"></div>
    </div>
    <div className="flex gap-2">
      <div className="h-8 bg-gray-200 rounded w-20"></div>
      <div className="h-8 bg-gray-200 rounded w-24"></div>
    </div>
  </div>
);

// リストローディング（複数のカード）
const ContentListLoading = ({ count = 3 }) => (
  <div className="space-y-4">
    {Array.from({ length: count }, (_, index) => (
      <CardLoadingSkeleton key={index} />
    ))}
  </div>
);

// プロップスタイプ定義
Spinner.propTypes = {
  size: PropTypes.oneOf(['small', 'default', 'large']),
  className: PropTypes.string
};

InlineLoading.propTypes = {
  message: PropTypes.string,
  size: PropTypes.oneOf(['small', 'default', 'large'])
};

FullAreaLoading.propTypes = {
  message: PropTypes.string,
  description: PropTypes.string,
  className: PropTypes.string
};

OverlayLoading.propTypes = {
  message: PropTypes.string,
  description: PropTypes.string,
  isVisible: PropTypes.bool
};

ContentListLoading.propTypes = {
  count: PropTypes.number
};

// 名前付きエクスポート
export {
  Spinner,
  InlineLoading,
  FullAreaLoading,
  OverlayLoading,
  ContentLoading,
  QuestionsLoading,
  SavingResults,
  AssigningContent,
  LoadingResults,
  CardLoadingSkeleton,
  ContentListLoading
};

// デフォルトエクスポート（主要なコンポーネント群）
const LoadingStates = {
  Spinner,
  InlineLoading,
  FullAreaLoading,
  OverlayLoading,
  ContentLoading,
  QuestionsLoading,
  SavingResults,
  AssigningContent,
  LoadingResults,
  ContentListLoading
};

export default LoadingStates; 